import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { FileText, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';

export default function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await api.get('/applications/my');
        setApplications(response.data || []);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);
  
  const filteredApps = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);
  
  const statusCounts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="text-yellow-500" size={16} />;
      case 'reviewed': return <Eye className="text-blue-500" size={16} />;
      case 'accepted': return <CheckCircle className="text-green-500" size={16} />;
      case 'rejected': return <XCircle className="text-red-500" size={16} />;
      default: return <FileText className="text-neutral-400" size={16} />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'reviewed': return 'bg-blue-100 text-blue-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="My Applications">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="My Applications">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'reviewed', 'accepted', 'rejected'].map((status) => (
          <button
            key={status}
            data-testid={`filter-${status}`}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-[#FF6B35] text-white'
                : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)} ({statusCounts[status]})
          </button>
        ))}
      </div>
      
      {filteredApps.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <FileText className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500 mb-2">
            {filter === 'all' 
              ? "You haven't applied to any opportunities yet" 
              : `No ${filter} applications`
            }
          </p>
          {filter === 'all' && (
            <Link
              to="/researcher/opportunities"
              className="text-[#FF6B35] font-medium hover:underline"
            >
              Browse Opportunities
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredApps.map((app) => (
            <div
              key={app.application_id}
              data-testid={`application-${app.application_id}`}
              className="bg-white border border-neutral-200 p-6 card-researcher"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/opportunity/${app.opportunity_id}`}
                    className="font-semibold text-lg text-neutral-900 hover:text-[#FF6B35] transition-colors"
                  >
                    {app.opportunity?.title}
                  </Link>
                  <p className="text-sm text-neutral-500 mt-1">
                    {app.opportunity?.client?.name || 'Anonymous Client'}
                  </p>
                  
                  {app.cover_letter && (
                    <div className="mt-3 bg-neutral-50 p-3 text-sm text-neutral-600">
                      <span className="font-medium text-neutral-700">Your cover letter:</span>
                      <p className="mt-1">{app.cover_letter}</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-neutral-400 mt-3">
                    Applied {new Date(app.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <span className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-sm ${getStatusColor(app.status)}`}>
                    {getStatusIcon(app.status)}
                    {app.status}
                  </span>
                  
                  {app.opportunity?.budget_max && (
                    <p className="text-sm text-neutral-500">
                      ₹{app.opportunity.budget_min?.toLocaleString()} - ₹{app.opportunity.budget_max?.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
